import { useContext, useMemo } from "react"
import PropTypes from "prop-types"
import { chain, compact, partition, noop } from "lodash"
import { OpenInNew } from "@mui/icons-material"
import SearchableMenu from "components/searchable-menu"
import { useIsOpPro, useAccountAlias } from "resources/account"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { hasPermission, PERMISSION_LEVELS } from "helpers/permissions"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "../../analytics"
import { ASSET_VIEW_EVENTS } from "connected-components/asset-view/analytics/events"
import { useMetricsSelectorAnalytics } from "components/custom-metrics-selector/hooks/use-metrics-selector-analytics"

import "./style.scss"

function getActions(account, template, setView, deleteTemplate, setShowMetricsSelector, track = noop) {
  const actions = []
  const hasWriteAccess = hasPermission(template.permissionLevel, PERMISSION_LEVELS.WRITE)
  if (hasWriteAccess) {
    actions.push({
      id: "edit",
      label: "Edit",
      onClick: () => {
        track(DASHBOARD_EVENTS.VIEW_OPENED, { templateId: template.id, templateName: template.name })
        setView(template.id)
        setShowMetricsSelector(true)
      },
    })
  }

  const hasDeleteAccess = hasPermission(template.permissionLevel, PERMISSION_LEVELS.OWNER)
  if (hasDeleteAccess) {
    actions.push(...[{
      id: "permissions",
      component: <div className="permissions-menu-item">
        <span>Permissions</span>
        <OpenInNew fontSize="small" />
      </div>,
      onClick: () => {
        track(DASHBOARD_EVENTS.VIEW_PERMISSIONS_CLICKED, { templateId: template.id, templateName: template.name })
        window.open(`/${account}/template/${template.id}/permissions`, "_blank")
      },
    }, {
      id: "delete",
      label: "Delete",
      onClick: async () => {
        await deleteTemplate(template)
        track(DASHBOARD_EVENTS.VIEW_DELETED, { templateId: template.id, templateName: template.name })
        setView("exposure")
      },
    }])
  }

  return actions
}

const GROUP_LABELS = {
  RISK_MODEL: "Risk Model",
  FACTOR_SENSITIVITY: "Factor Sensitivity",
  LICENSED_CONTENT_SETS: "Licensed",
  PROPRIETARY_CONTENT_SETS: "Proprietary",
  CUSTOM: "Custom",
}

const GROUP_IDS = {
  RISK_MODEL: "riskModel",
  FACTOR_SENSITIVITY: "factorSensitivity",
  LICENSED_CONTENT_SETS: "licensedContentSets",
  PROPRIETARY_CONTENT_SETS: "proprietaryContentSets",
  CUSTOM: "custom",
}

export default function AssetsViewSelector({ context, templates, contentSets }) {
  const { view, setView, setShowMetricsSelector, deleteTemplate } = useContext(context)
  // TODO: This should be replaced by the proper Analytics context
  const analytics = useAnalytics()
  const assetViewContext = useContext(context)
  const metricsSelectorAnalytics = useMetricsSelectorAnalytics(assetViewContext)

  const isOpPro = useIsOpPro()
  const account = useAccountAlias()
  const [proprietaryContentSets, licensedContentSets] = useMemo(() => partition(contentSets, "proprietary"), [contentSets])
  const defaultMenuItems = useMemo(() => compact([
    {
      id: "summary",
      label: "Summary",
      groupId: GROUP_IDS.RISK_MODEL,
      groupLabel: GROUP_LABELS.RISK_MODEL,
    }, {
      id: "exposure",
      label: "Exposure",
      groupId: GROUP_IDS.RISK_MODEL,
      groupLabel: GROUP_LABELS.RISK_MODEL,
    }, {
      id: "performance",
      label: "Performance",
      groupId: GROUP_IDS.RISK_MODEL,
      groupLabel: GROUP_LABELS.RISK_MODEL,
    }, !isOpPro && {
      id: "risk",
      label: "Risk",
      groupId: GROUP_IDS.RISK_MODEL,
      groupLabel: GROUP_LABELS.RISK_MODEL,
    }, {
      id: "beta",
      label: "Beta",
      groupId: GROUP_IDS.RISK_MODEL,
      groupLabel: GROUP_LABELS.RISK_MODEL,
    }, {
      id: "marginalContribution",
      label: "Marginal Contribution",
      groupId: GROUP_IDS.RISK_MODEL,
      groupLabel: GROUP_LABELS.RISK_MODEL,
    }, {
      id: "predictedFactorSensitivity",
      label: "Predicted Factor Sensitivity",
      groupId: GROUP_IDS.FACTOR_SENSITIVITY,
      groupLabel: GROUP_LABELS.FACTOR_SENSITIVITY,
    }, {
      id: "historicalFactorSensitivity",
      label: "Historical Factor Sensitivity",
      groupId: GROUP_IDS.FACTOR_SENSITIVITY,
      groupLabel: GROUP_LABELS.FACTOR_SENSITIVITY,
    },
  ]), [isOpPro])

  const contentSetMenuItems = useMemo(() => ([
    ...chain(licensedContentSets)
      .sortBy("label")
      .map((contentSet) => ({
        id: contentSet.id,
        label: contentSet.name,
        groupId: GROUP_IDS.LICENSED_CONTENT_SETS,
        groupLabel: GROUP_LABELS.LICENSED_CONTENT_SETS,
      }))
      .value(),
    ...chain(proprietaryContentSets)
      .sortBy("label")
      .map((contentSet) => ({
        id: contentSet.id,
        label: contentSet.name,
        groupId: GROUP_IDS.PROPRIETARY_CONTENT_SETS,
        groupLabel: GROUP_LABELS.PROPRIETARY_CONTENT_SETS,
      }))
      .value(),
  ]), [licensedContentSets, proprietaryContentSets])

  const customMenuItems = useMemo(() => ([
    {
      id: "custom",
      label: "New View...",
      groupId: GROUP_IDS.CUSTOM,
      groupLabel: GROUP_LABELS.CUSTOM,
      onSelect: () => {
        metricsSelectorAnalytics.trackOpened()
        setShowMetricsSelector(true)
      },
    },
    ...chain(templates)
      .sortBy("label")
      .map((template) => ({
        id: template.id,
        label: template.name,
        groupId: GROUP_IDS.CUSTOM,
        groupLabel: GROUP_LABELS.CUSTOM,
        actions: getActions(account, template, setView, deleteTemplate, setShowMetricsSelector, analytics?.track),
      }))
      .value(),
  ]), [setShowMetricsSelector, setView, templates, deleteTemplate, account, metricsSelectorAnalytics, analytics?.track])

  const menuItems = useMemo(() => (
    [...customMenuItems, ...defaultMenuItems, ...contentSetMenuItems].map((item, index) => ({ ...item, order: index + 1 }))
  ), [defaultMenuItems, contentSetMenuItems, customMenuItems])

  return <SearchableMenu
    items={menuItems}
    current={view}
    onSelect={(selectedItem) => {
      if (selectedItem.onSelect) {
        selectedItem.onSelect()
      }
      analytics.track(ASSET_VIEW_EVENTS.TAB_SELECTED, { selectedTab: selectedItem.id })
      setView(selectedItem.id)
    }}
  />
}

AssetsViewSelector.propTypes = {
  context: PropTypes.object.isRequired,
  templates: PropTypes.array.isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
}
